import { DestroyRef, inject, Injectable } from '@angular/core';
import { first } from 'rxjs';
import { isArray, isObject, mapValues, pickBy } from 'lodash';
import { GoogleAnalyticsActions, GoogleAnalyticsDataLayerParameters, GoogleAnalyticsEvent } from '../types';
import { SessionStorageService, STORAGE_KEY_DATA_LAYER_DETAILS } from '@lx/shared/storage';
import { DATA_LAYER_SERVICE } from '../tokens/data-layer.token';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';

declare const window: Window & { dataLayer: Partial<GoogleAnalyticsEvent>[] };

@Injectable({
  providedIn: 'root',
})
export class GoogleAnalyticsService {
  private readonly sessionStorageService = inject(SessionStorageService);
  private readonly googleAnalyticsDataLayerService = inject(DATA_LAYER_SERVICE);

  private readonly destroyRef = inject(DestroyRef);

  constructor() {
    window.dataLayer = window.dataLayer || [];
  }

  trackViewEvent(event: string) {
    this.trackEvent(event, GoogleAnalyticsActions.VIEW);
  }

  trackClickEvent(event: string) {
    this.trackEvent(event, GoogleAnalyticsActions.CLICK);
  }

  trackEvent(event: string, action: GoogleAnalyticsActions | string) {
    // If there is no event or action, nothing to track
    if (!event || !action) {
      return;
    }

    this.googleAnalyticsDataLayerService
      .fillDataLayer()
      .pipe(first(), takeUntilDestroyed(this.destroyRef))
      .subscribe((dataLayer) => {
        const combinedDataLayer = this.cleanAndCombineDataLayer(dataLayer);
        this.sessionStorageService.set(STORAGE_KEY_DATA_LAYER_DETAILS, combinedDataLayer);
        const gaEvent = <GoogleAnalyticsEvent>{ event, action, ...combinedDataLayer };
        window.dataLayer.push(this.cleanNotDefinedFields(gaEvent));
      });
  }

  private getDataLayerFromSessionStorage(): Partial<GoogleAnalyticsDataLayerParameters> | undefined {
    return this.sessionStorageService.get(STORAGE_KEY_DATA_LAYER_DETAILS);
  }

  private cleanAndCombineDataLayer(dataLayer: Partial<GoogleAnalyticsDataLayerParameters>): Partial<GoogleAnalyticsDataLayerParameters> {
    const cleanedDataLayer = this.cleanNotDefinedFields(dataLayer);
    const sessionData = this.getDataLayerFromSessionStorage();
    return this.googleAnalyticsDataLayerService.deepMerge(sessionData, cleanedDataLayer);
  }

  // Method to clean undefined and null values for Partial<GoogleAnalyticsEvent>
  private cleanNotDefinedFields(data: Partial<GoogleAnalyticsDataLayerParameters>): Partial<GoogleAnalyticsDataLayerParameters> {
    // Helper function to clean values recursively
    const cleanRecursive = (item: unknown): unknown => {
      if (isArray(item)) {
        return item
          .map(cleanRecursive) // Recursively clean each item in the array
          .filter((v): v is Exclude<typeof v, undefined | null | string> => v !== undefined && v !== null && v !== 'NaN'); // Filter out undefined and null values
      } else if (isObject(item) && item !== null) {
        return mapValues(
          pickBy(item, (v) => v !== undefined && v !== null && v !== 'NaN'), // Remove undefined and null values from the object
          (value) => cleanRecursive(value), // Recursively clean nested values
        );
      }
      return item;
    };

    return cleanRecursive(data) as Partial<GoogleAnalyticsDataLayerParameters>;
  }
}
